/* eslint-disable no-alert */
import axios from 'axios';

import { types as eventTypes } from './enums/event';

/** Combine all panel canvas elements into a single canvas for use in printing or saving */
const _getExportCanvas = (core, args = {}) => {
	core.eventEmitter.emit(eventTypes.EXPORT_CANVAS_START);

	const { size } = core.panels[0];
	const rootNode = core.rootModchart.node();
	const isStyled = !!getComputedStyle(rootNode)['width'];
	const bBox = rootNode.getBoundingClientRect();
	const { height: origHeight, width: origWidth } = bBox;
	const bgColor = _getVisibleBackgroundColor(rootNode);
	const ratio = core.getScreenRatio();

	args.height = args.height || 0;
	args.width = args.width || (size.width + size.padding.left + size.padding.right);

	if (args.height === 0) {
		// get total height
		core.panels.forEach(panel => {
			args.height +=
				panel.size.height +
				panel.size.padding.top +
				panel.size.padding.bottom;
		});
	}

	// set new dimensions
	rootNode.style.height = args.height;
	rootNode.style.width = args.width;

	core.resize();
	core.render();

	args.header = _getExportCanvasHeader();
	args.footer = _getExportCanvasFooter();

	const canvasHeight = args.height + args.header.height + args.footer.height + 1;

	// create canvas inside iframe
	const canvas = document.createElement('canvas');
	canvas.setAttribute('width', args.width * ratio);
	canvas.setAttribute('height', canvasHeight * ratio);
	canvas.style.width = args.width;
	canvas.style.height = canvasHeight;
	const ctx = canvas.getContext('2d');


	ctx.rect(0, 0, args.width * ratio, canvasHeight * ratio);
	ctx.fillStyle = bgColor;
	ctx.fill();

	// fill canvas with content
	_applyExportCanvasContent(core, ctx, args);

	// reset dimensions back to original
	rootNode.style.height = isStyled ? origHeight : '';
	rootNode.style.width = isStyled ? origWidth : '';

	core.resize();
	core.render();

	core.eventEmitter.emit(eventTypes.EXPORT_CANVAS_END);

	return canvas;
};

/**
 * Overridable method to add custom header content to an exported canvas
 * height = intended height of header section
 * applyContent = function reference that will receive a canvas context to draw into
 */
const _getExportCanvasHeader = () => {
	// default
	return {
		height: 0,
		applyContent: () => {
			return;
		}
	};
};

/**
 * Overridable method to add custom footer content to an exported canvas
 * height = intended height of footer section
 * applyContent = function reference that will receive a canvas context to draw into
 */
const _getExportCanvasFooter = () => {
	// default
	return {
		height: 0,
		applyContent: () => {
			return;
		}
	};
};

/** Draw each panel into the temporary canvas context */
const _applyExportCanvasContent = (core, context, args) => {
	let y = args.header
		? args.header.height
		: 0;
	const ratio = core.getScreenRatio();

	// optional header content
	if (args.header) args.header.applyContent(context, 0);

	// default font
	if (!args.font) args.font = '11px Arial';

	// init context
	context.font = args.font;
	context.textBaseline = 'top';
	context.textAlign = 'left';

	// add legend text (which is normally drawn using HTML) to each panel canvas
	core.panels.forEach(panel => {
		context.drawImage(panel.rootCanvas.node(), 0, y);
		context.drawImage(panel.rootTools.node(), 0, y);

		// draw legend content for this panel
		_applyExportCanvasLegendContent(core, context, panel, y);

		y +=
			(
				Math.round(panel.size.height) +
				panel.size.padding.top +
				Math.max(
					panel.size.padding.bottom,
					panel.size.margin.bottom
				)
			) *
			ratio;
	});

	// optional footer content
	if (args.footer) args.footer.applyContent(context, y);
};

/** Overridable method to draw a legend for a single panel into the export canvas */
const _applyExportCanvasLegendContent = (core, context, panel, top) => {
	let inputs = [];
	let xLeft = panel.size.padding.left + 5;
	let label = '';
	let symbol = '';

	for (const indicator of panel.indicators) {
		inputs = [];

		for (const input of indicator.params.inputs) {
			inputs.push(input.value);
		}

		// text label
		if (indicator.params.id === 'price') {
			// price labels should include company name
			symbol = indicator.params.symbol;

			label = symbol
				? core.xref[symbol].companyName
				: indicator.params.symbol;
		} else {
			// all other indicators
			label =
				(indicator.params.name || '') +
				(inputs.length !== 0 ? ` (${inputs.join(',')})` : '');
		}

		context.fillStyle =
			indicator.params.style.color ||
			(indicator.params.style.background && indicator.params.style.background.color) ||
			'#666';
		context.fillText(label, xLeft, top + 2);

		xLeft += context.measureText(label).width + 10;
	}
};

/** Get first non-transparent backgroundColor from DOM ancestors */
const _getVisibleBackgroundColor = el => {
	let bgColor = null;

	while (!bgColor && el.parentNode !== document) {
		// Get the color from the element attributes
		const color = el.style.backgroundColor;
		if (color.split(',').length !== 4 && color !== 'transparent') bgColor = color;

		el = el.parentNode;
	}

	return bgColor || '#FFFFFF';
};

/** Combine canvas panels and print hidden iframe */
export const print = (core, args = {}) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	core.render();

	let printFrame = document.createElement('iframe');
	const marginTop = 80;
	const canvas = _getExportCanvas(core, args);
	const printHeader = _getPrintHeader();
	printFrame.setAttribute('height', core.size.height);
	printFrame.setAttribute('width', core.size.width);
	printFrame.style.visibility = 'hidden';
	printFrame.style.zIndex = -1;
	printFrame.addEventListener('load', () => {
		const doc = printFrame.contentDocument;
		const body = doc.body;
		// append custom print-only header, if it exists
		if (printHeader) {
			body.append(printHeader);
		}
		// append new canvas
		canvas.style.position = 'absolute';
		canvas.style.top = marginTop;

		body.appendChild(canvas);

		// firefox likes a setTimeout here
		window.setTimeout(() => {
			// invoke print from within iframe
			printFrame.contentWindow.focus();

			if (
				!printFrame.contentWindow.document.execCommand(
					'print',
					false,
					null
				)
			) {
				printFrame.contentWindow.print();
			}

			// remove hidden iframe
			printFrame.parentElement.removeChild(printFrame);
			printFrame = null;
		}, 0);
	});

	document.body.appendChild(printFrame);
};

/**
 * Custom print header - can be overridden locally
 * TODO: revisit a better way of doing this - overriding methods is the old V1 pattern.
 */
const _getPrintHeader = () => {
	return '';
};

/** Store down image on server */
export const saveImage = async (core, args) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	args = args || {
		width: core.size.width,
		height: core.size.height
	};

	const canvas = _getExportCanvas(core, args);
	const canvasB64 = canvas.toDataURL();
	const inputs = {
		json: canvasB64,
		name: args.name
	};

	// optional id
	if (args.id && args.id.length) {
		inputs.id = args.id;
	}

	try {
		const response = await axios.post(
			`${core.params.apiPath}chartapi/saveimage`,
			inputs,
			{ headers: core._getXHRHeaders() }
		);

		const result = core.parseDebugInfo(response.request.responseText);

		if (args.onComplete) {
			args.onComplete(result);
		} else if (result.Id) {
			const url = `${core.params.apiPath}chartapi/loadimage/${result.Id}`;
			core.eventEmitter.emit(eventTypes.IMAGE_SAVE_SUCCESS, { result, url });
			return {
				args,
				id: result.Id,
				result,
				url
			};
		} else {
			core.eventEmitter.emit(eventTypes.IMAGE_SAVE_ERROR, { result });
			throw new Error(
				"Can't complete request to save image"
			);
		}
	} catch (error) {
		window.alert(`An error occurred.\n\n${error.message}`);
	}
	return true;
};

/** Temporarily store image, send down with custom filename, then delete image. */
export const downloadImage = async (core, args = {}) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	const canvas = _getExportCanvas(core, args);
	const canvasB64 = canvas.toDataURL();

	try {
		const response = await axios.post(
			`${core.params.apiPath}chartapi/saveimage`,
			{ json: canvasB64 },
			{ headers: core._getXHRHeaders() }
		);

		const result = core.parseDebugInfo(response.request.responseText);

		if (result.Id) {
			let url = `${core.params.apiPath}chartapi/loadimage/${result.Id}?download=true&remove=true`;

			if (args.name) url += `&filename=${args.name}`;

			if (core.state.authToken) url += `&access_token=${core.state.authToken}`;

			// Set URL to initiate download
			window.location.href = url;

			return {
				args,
				id: result.Id,
				result
			};
		}
		throw new Error("Can't complete request to save image");
	} catch (error) {
		window.alert(`An error occurred.\n\n${error.message}`);
	}
	return true;
};

export default {
	downloadImage,
	print,
	saveImage
};
